/**
 * AllocationsTable — répartition d'un paiement ou d'une note de crédit sur les factures ouvertes (reportées) du tiers.
 * `open` = documents avec solde ; `alloc` = {doc_id: montant} ; `amount` = montant à répartir.
 */
import { Wand2 } from "lucide-react";
import { r2 } from "@/lib/format";
import { Btn, inputCls, money, today } from "../comptabilite/_shared";

export function autoAllocate(open, amount) {
  let rest = r2(amount);
  const next = {};
  for (const d of open || []) { if (rest <= 0.005) break; const a = Math.min(rest, d.balance); next[d.id] = r2(a); rest = r2(rest - a); }
  return next;
}

export function AllocationsTable({ title, open, alloc, setAlloc, amount, hasParty, disabled, testId = "allocations" }) {
  return (
    <div className="border rounded-xl overflow-hidden" data-testid={testId}>
      <div className="px-3 py-2 bg-slate-50 border-b text-xs font-bold uppercase tracking-wide text-slate-700 flex items-center justify-between">
        <span>{title} ({open?.length ?? "…"})</span>
        {!disabled && <Btn tone="ghost" icon={Wand2} onClick={() => setAlloc(autoAllocate(open, amount))} disabled={!open?.length || !(amount > 0)} testId={`${testId}-auto`}>Répartir automatiquement</Btn>}
      </div>
      {open?.length ? (
        <table className="w-full text-xs">
          <thead className="bg-white text-[10px] uppercase tracking-wider text-slate-400"><tr><th className="text-left px-3 py-1.5 w-24">Date</th><th className="text-left px-2 py-1.5 w-24">N°</th><th className="text-left px-2 py-1.5">Description</th><th className="text-right px-2 py-1.5 w-28">Solde</th><th className="text-right px-2 py-1.5 w-36">Appliquer</th></tr></thead>
          <tbody className="divide-y">
            {open.map((d) => (
              <tr key={d.id} data-testid={`${testId}-doc-${d.id}`}>
                <td className="px-3 py-1.5 font-mono">{d.date}</td>
                <td className="px-2 py-1.5 font-mono">{d.number || "—"}</td>
                <td className="px-2 py-1.5 text-slate-600">{d.description || "—"}{d.due_date && d.due_date < today() && <span className="ml-1 text-rose-600">(en retard)</span>}</td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums">{money(d.balance)}</td>
                <td className="px-2 py-1.5">
                  <div className="flex items-center gap-1 justify-end">
                    <input type="number" step="0.01" min="0" max={d.balance} value={alloc[d.id] ?? ""} onChange={(e) => setAlloc((p) => ({ ...p, [d.id]: e.target.value }))} disabled={disabled} className={`${inputCls} w-28 text-right font-mono py-1`} data-testid={`${testId}-amount-${d.id}`} />
                    {!disabled && <button type="button" onClick={() => setAlloc((p) => ({ ...p, [d.id]: d.balance }))} className="text-[10px] text-teal-700 hover:underline whitespace-nowrap" data-testid={`${testId}-full-${d.id}`}>solde</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : open && <p className="px-3 py-3 text-xs text-slate-400">{hasParty ? "Aucun document reporté avec solde pour ce tiers — le montant restera non appliqué (acompte / crédit au compte)." : "Choisissez d'abord un tiers."}</p>}
    </div>
  );
}
